import { useState } from "react";
import type { ActionItem } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Filter, CheckCircle2, Sparkles, Loader2, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";

const impactColor = { low: "bg-info/20 text-info", medium: "bg-warning/20 text-warning", high: "bg-destructive/20 text-destructive" };
const difficultyColor = { low: "bg-success/20 text-success", medium: "bg-warning/20 text-warning", high: "bg-destructive/20 text-destructive" };
const categoryLabel = { seo: "SEO", "ai-visibility": "AI Visibility", authority: "Authority", technical: "Technical" };
const categoryColor = { seo: "bg-primary/20 text-primary", "ai-visibility": "bg-accent/20 text-accent", authority: "bg-info/20 text-info", technical: "bg-warning/20 text-warning" };

interface CompletedActionsHook {
  completedIds: Set<string>;
  toggleCompleted: (id: string) => void;
}

interface ActionPlanTabProps {
  data: ActionItem[];
  completedActions?: CompletedActionsHook;
  /** When provided, "Keep" persists the enhanced suggestion to analyses.results in DB. */
  analysisId?: string;
  /** Page URL passed to the AI for grounded suggestions. */
  pageUrl?: string;
  /** Full crawled page markdown — used to build a relevant excerpt for AI enhancement. */
  pageContent?: string;
  /** Notify parent when an action's suggestion is updated locally so it can re-render. */
  onActionUpdated?: (updated: ActionItem) => void;
}

export const ActionPlanTab = ({ data, completedActions, analysisId, pageUrl, pageContent, onActionUpdated }: ActionPlanTabProps) => {
  const [filterCat, setFilterCat] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("impact");

  // Local mirror so optimistic suggestion updates render even when parent doesn't refresh
  const [overrides, setOverrides] = useState<Record<string, Partial<ActionItem>>>({});

  const merged = data.map((a) =>
    overrides[a.id] ? { ...a, ...overrides[a.id] } : a
  );

  // Fallback to local state when no shared hook is provided (e.g. unauthenticated view)
  const [localCompletedIds, setLocalCompletedIds] = useState<Set<string>>(new Set());
  const completedIds = completedActions?.completedIds ?? localCompletedIds;
  const toggleCompleted = completedActions?.toggleCompleted ?? ((id: string) => {
    setLocalCompletedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  });

  const filtered = merged.filter((a) => filterCat === "all" || a.category === filterCat);
  const sorted = [...filtered].sort((a, b) => {
    const aCompleted = completedIds.has(a.id) ? 1 : 0;
    const bCompleted = completedIds.has(b.id) ? 1 : 0;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;
    const impactOrder = { high: 3, medium: 2, low: 1 };
    if (sortBy === "impact") return impactOrder[b.impact] - impactOrder[a.impact];
    if (sortBy === "difficulty") return impactOrder[a.difficulty] - impactOrder[b.difficulty];
    return 0;
  });

  const completedCount = merged.filter((a) => completedIds.has(a.id)).length;
  const progressPercent = merged.length > 0 ? (completedCount / merged.length) * 100 : 0;

  const handleSuggestionUpdated = (id: string, patch: Partial<ActionItem>) => {
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    const base = data.find((a) => a.id === id);
    if (base) onActionUpdated?.({ ...base, ...overrides[id], ...patch });
  };

  return (
    <div className="space-y-4 mt-4">
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="text-sm font-medium">{completedCount} of {merged.length} completed</span>
            </div>
            <span className="text-xs text-muted-foreground">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="seo">SEO</SelectItem>
              <SelectItem value="ai-visibility">AI Visibility</SelectItem>
              <SelectItem value="authority">Authority</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="impact">Sort by Impact</SelectItem>
            <SelectItem value="difficulty">Sort by Ease</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {sorted.map((action) => (
        <ActionCard
          key={action.id}
          action={action}
          isCompleted={completedIds.has(action.id)}
          onToggle={() => toggleCompleted(action.id)}
          analysisId={analysisId}
          pageUrl={pageUrl}
          pageContent={pageContent}
          onSuggestionUpdated={(patch) => handleSuggestionUpdated(action.id, patch)}
        />
      ))}
    </div>
  );
};

interface ActionCardProps {
  action: ActionItem;
  isCompleted: boolean;
  onToggle: () => void;
  analysisId?: string;
  pageUrl?: string;
  pageContent?: string;
  onSuggestionUpdated: (patch: Partial<ActionItem>) => void;
}

const ActionCard = ({ action, isCompleted, onToggle, analysisId, pageUrl, pageContent, onSuggestionUpdated }: ActionCardProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Card className={`border-border bg-card transition-opacity ${isCompleted ? "opacity-60" : ""}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
            <div className="flex items-start gap-3">
              <div
                className="pt-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              >
                <Checkbox checked={isCompleted} />
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium w-fit ${categoryColor[action.category]}`}>
                  {categoryLabel[action.category]}
                </span>
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="flex-1">
                    <CardTitle className={`text-sm leading-tight ${isCompleted ? "line-through text-muted-foreground" : ""}`}>{action.title}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${impactColor[action.impact]}`}>
                      Impact: {action.impact}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${difficultyColor[action.difficulty]}`}>
                      Effort: {action.difficulty}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Evidence</div>
              <p className="text-xs text-foreground bg-secondary/30 p-2 rounded">{action.evidence}</p>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Steps</div>
              <ol className="space-y-1">
                {action.steps.map((s, i) => (
                  <li key={i} className="text-xs text-foreground flex gap-2">
                    <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{i + 1}</Badge>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            {action.contentSuggestion && (
              <EnhanceableSuggestion
                kind="content"
                action={action}
                original={action.contentSuggestion}
                analysisId={analysisId}
                pageUrl={pageUrl}
                pageContent={pageContent}
                onKept={(text) => onSuggestionUpdated({ contentSuggestion: text })}
              />
            )}
            {action.codeSuggestion && (
              <EnhanceableSuggestion
                kind="code"
                action={action}
                original={action.codeSuggestion}
                analysisId={analysisId}
                pageUrl={pageUrl}
                pageContent={pageContent}
                onKept={(text) => onSuggestionUpdated({ codeSuggestion: text })}
              />
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

interface EnhanceableSuggestionProps {
  kind: "content" | "code";
  action: ActionItem;
  original: string;
  analysisId?: string;
  pageUrl?: string;
  pageContent?: string;
  onKept: (text: string) => void;
}

/**
 * Build a ~3000-char excerpt of the page markdown that is most likely to
 * contain content relevant to the action being enhanced.
 *
 * Strategy:
 *  1. Always include the first ~800 chars (intro / hero usually matters).
 *  2. If the action references a structural section (faq, schema, footer,
 *     heading, nav, etc.), grab that section by `##` boundaries.
 *  3. Otherwise, find the best keyword match for the action's title +
 *     evidence and grab ±500 chars around it.
 *  4. Fallback to the first 3000 chars if no match is found.
 */
const buildRelevantExcerpt = (markdown: string | undefined, action: ActionItem): string => {
  if (!markdown) return "";
  const MAX = 3000;
  if (markdown.length <= MAX) return markdown;

  const intro = markdown.slice(0, 800);
  const remainingBudget = MAX - intro.length - 20; // 20 chars for separator

  const lower = markdown.toLowerCase();
  const haystack = `${action.title} ${action.evidence}`.toLowerCase();

  // Section keywords → match a markdown heading line containing the keyword
  const sectionKeywords = ["faq", "schema", "footer", "header", "nav", "pricing", "feature", "testimonial", "review", "spec", "comparison", "about", "contact"];
  const matchedSection = sectionKeywords.find((kw) => haystack.includes(kw));
  if (matchedSection) {
    // Find a markdown heading line containing the section keyword
    const headingRegex = new RegExp(`^#{1,6}\\s+.*${matchedSection}.*$`, "im");
    const m = markdown.match(headingRegex);
    if (m && typeof m.index === "number" && m.index >= 800) {
      const start = m.index;
      const section = markdown.slice(start, start + remainingBudget);
      return `${intro}\n\n…\n\n${section}`;
    }
  }

  // Keyword-match: pick the most distinctive word from the action and find it
  const stopwords = new Set(["the","a","an","and","or","of","to","in","is","for","on","with","by","this","that","your","you","it","at","be","as","are","was","were","but","not"]);
  const candidates = haystack
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 5 && !stopwords.has(w));

  let bestIdx = -1;
  for (const word of candidates) {
    const idx = lower.indexOf(word, 800);
    if (idx !== -1) {
      bestIdx = idx;
      break;
    }
  }

  if (bestIdx !== -1) {
    const half = Math.floor(remainingBudget / 2);
    const start = Math.max(800, bestIdx - half);
    const snippet = markdown.slice(start, start + remainingBudget);
    return `${intro}\n\n…\n\n${snippet}`;
  }

  // Fallback: first MAX chars
  return markdown.slice(0, MAX);
};

const EnhanceableSuggestion = ({ kind, action, original, analysisId, pageUrl, pageContent, onKept }: EnhanceableSuggestionProps) => {
  const navigate = useNavigate();
  const { plan } = useSubscription();
  const canEnhance = plan === "starter" || plan === "growth";

  const [enhanced, setEnhanced] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const label = kind === "content" ? "Content Suggestion" : "Code Example";

  const renderOriginal = () =>
    kind === "code" ? (
      <pre className="text-xs text-foreground bg-background p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">{original}</pre>
    ) : (
      <p className="text-xs text-foreground bg-primary/5 p-2 rounded border border-primary/10 whitespace-pre-wrap">{original}</p>
    );

  const renderEnhanced = (text: string) =>
    kind === "code" ? (
      <pre className="text-xs text-foreground bg-background p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap border border-primary/30">{text}</pre>
    ) : (
      <p className="text-xs text-foreground bg-primary/10 p-2 rounded border border-primary/30 whitespace-pre-wrap">{text}</p>
    );

  const handleEnhance = async () => {
    setLoading(true);
    try {
      const excerpt = buildRelevantExcerpt(pageContent, action);
      const { data, error } = await supabase.functions.invoke("enhance-action-suggestion", {
        body: {
          type: kind,
          action,
          pageUrl,
          pageContentExcerpt: excerpt || undefined,
          pageContentLength: pageContent?.length,
        },
      });
      if (error) throw error;
      const errMsg = (data as any)?.error;
      if (errMsg) {
        toast({ title: "Couldn't enhance", description: errMsg, variant: "destructive" });
        return;
      }
      const text = (data as any)?.enhanced as string | undefined;
      if (!text) {
        toast({ title: "Empty response", description: "AI returned no content.", variant: "destructive" });
        return;
      }
      setEnhanced(text);
    } catch (e: any) {
      toast({
        title: "Enhance failed",
        description: e?.message ?? "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeep = async () => {
    if (!enhanced) return;
    if (!analysisId) {
      // No DB row to update — keep in-session only
      onKept(enhanced);
      setEnhanced(null);
      toast({ title: "Saved for this session" });
      return;
    }
    setSaving(true);
    try {
      // Read current results, patch the matching action, write back
      const { data: row, error: readErr } = await supabase
        .from("analyses")
        .select("results")
        .eq("id", analysisId)
        .maybeSingle();
      if (readErr) throw readErr;
      if (!row) throw new Error("Analysis not found");

      const results = row.results as any;
      const plan = Array.isArray(results?.actionPlan) ? results.actionPlan : [];
      const idx = plan.findIndex((a: ActionItem) => a.id === action.id);
      if (idx === -1) throw new Error("Action not found in saved analysis");

      if (kind === "content") plan[idx].contentSuggestion = enhanced;
      else plan[idx].codeSuggestion = enhanced;

      const { error: updErr } = await supabase
        .from("analyses")
        .update({ results: { ...results, actionPlan: plan } })
        .eq("id", analysisId);
      if (updErr) throw updErr;

      onKept(enhanced);
      setEnhanced(null);
      toast({ title: "Suggestion updated" });
    } catch (e: any) {
      toast({
        title: "Couldn't save",
        description: e?.message ?? "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setEnhanced(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        {canEnhance ? (
          !enhanced && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs px-2 gap-1.5"
              onClick={handleEnhance}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Enhancing…
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" />
                  Enhance with AI
                </>
              )}
            </Button>
          )
        ) : (
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            <Lock className="w-3 h-3" />
            Upgrade to Starter to enhance
          </button>
        )}
      </div>

      {!enhanced ? (
        renderOriginal()
      ) : (
        <div className="space-y-2">
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Original</div>
              {renderOriginal()}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-primary mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI Enhanced
              </div>
              {renderEnhanced(enhanced)}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={handleDiscard}
              disabled={saving}
            >
              Discard
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={handleKeep}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Keep
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
